import { useTailgateGrid } from '../hooks/useTailgateGrid'
import { useUpdateTailgateGrid } from '../hooks/useUpdateTailgateGrid'
import { usePermissions } from '@/hooks/usePermissions'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Flex } from '@chakra-ui/react'

import { Button } from '@/components/ui'
import { BUTTON_PRESS } from '@/utils/analytics'

import { getGridProperties } from '../utils/utils'
import { GridCameraFrame } from './GridCameraFrame'
import { GridCoordsBox } from './GridCoordsBox'

interface GridModalBodyIProps {
  hideActions?: boolean
  onClose: () => void
  onOpenTestModal?: () => void
}

export const GridModalBody = ({
  hideActions,
  onClose,
  onOpenTestModal,
}: GridModalBodyIProps) => {
  const { shouldEnableConfigureTailgateGrid, isImplementer } = usePermissions()
  const {
    outBox,
    inBox,
    isInitialGridLoading,
    isImageLoading,
    imageUrl,
    isDirty,
    associateDeviceValues,
    onSaveCallback,
    trackTailgateEvent,
    videoDimensions,
  } = useTailgateGrid()

  const { isUpdateLoading, updateGrid, deleteGrid } = useUpdateTailgateGrid({
    associatedDevices: associateDeviceValues,
    onClose,
    onSaveCallback,
  })

  const isLoading = isImageLoading || isInitialGridLoading

  // If existing grid is edited boxes should be geometrically valid or empty (this unset grid when saved)
  // If grid is set for the first time boxes should be gemetrically valid but cannot be empty
  const { isGridValid, isGridEmpty } = getGridProperties(inBox, outBox)

  const onSave = () => {
    trackTailgateEvent(`${BUTTON_PRESS} Tailgate Editor - Save Changes`)

    isGridEmpty ? deleteGrid() : updateGrid(inBox, outBox, videoDimensions)
  }

  const onCancel = () => {
    trackTailgateEvent(
      `${BUTTON_PRESS} Tailgate Editor - Tailgate Editor - Cancel`
    )
    onClose()
  }

  // Submit button should be disabled when:
  const isSubmitDisabled =
    !shouldEnableConfigureTailgateGrid || // User has role without edit permission
    isLoading || // Frame or initial grid are loading
    !imageUrl || // Frame cannot be loaded
    !isGridValid || // Grid is not setup correctly
    !isDirty // Grid has not been edited

  return (
    <Flex direction='column'>
      <Flex direction='row' pb={3}>
        <GridCameraFrame imageUrl={imageUrl} isImageLoading={isImageLoading} />
        <Flex direction='column' flex={1}>
          <GridCoordsBox
            backgroundColour='#D3F8EF'
            box={inBox}
            iconColour='#006C53'
            isValid={inBox?.isValid}
            loading={isInitialGridLoading}
            subTitle='First grid to capture motion, place close to reader, unsecured side.'
            title='Unsecured'
          />
          <GridCoordsBox
            backgroundColour='#FFDAD6'
            box={outBox}
            iconColour='#BA2D1A'
            isValid={outBox?.isValid}
            loading={isInitialGridLoading}
            subTitle='Second grid to capture motion, place to secured side.'
            title='Secured'
          />
        </Flex>
      </Flex>
      {!hideActions && (
        <Flex
          alignItems='center'
          direction='row'
          justifyContent='space-between'
          p={3}
        >
          <Flex alignItems='center' gap={2}>
            {isImplementer && (
              <Button onClick={onOpenTestModal} variant='secondary'>
                Enter Grid Testing Zone
              </Button>
            )}
          </Flex>
          <Flex direction='row'>
            <Button mr={3} onClick={onCancel} variant='secondary'>
              Cancel
            </Button>
            <Button
              isDisabled={isSubmitDisabled}
              isLoading={isUpdateLoading}
              leftIcon={<HiOutlineCheckCircle size='24px' />}
              loadingText='Saving'
              onClick={onSave}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
